import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

interface DialogData {
  rule: any;
  staffName: string;
  weekDays: any[];
}

interface BulkAvailabilityRuleDialogResult {
  weekDays: number[];
  startTime: string;
  endTime: string;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
}

@Component({
    selector: 'app-availability-rule-dialog',
    templateUrl: './availability-rule-dialog.component.html',
    styleUrls: ['./availability-rule-dialog.component.css'],
    standalone: false
})
export class AvailabilityRuleDialogComponent implements OnInit {
  ruleForm: UntypedFormGroup;

  constructor(
    private fb: UntypedFormBuilder,
    public dialogRef: MatDialogRef<AvailabilityRuleDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData
  ) {}

  ngOnInit(): void {
    this.buildForm();
  }

  private buildForm(): void {
    const isEditMode = !!this.data.rule;
    const selectedWeekDay = isEditMode
      ? this.resolveWeekDayOption(this.data.rule.weekDay)
      : '';

    this.ruleForm = this.fb.group({
      weekDay: [selectedWeekDay, isEditMode ? Validators.required : []],
      weekDays: [[], !isEditMode ? Validators.required : []],
      startTime: [this.data.rule ? this.data.rule.startTime : '', Validators.required],
      endTime: [this.data.rule ? this.data.rule.endTime : '', Validators.required],
      effectiveFrom: [this.data.rule ? this.data.rule.effectiveFrom : ''],
      effectiveTo: [this.data.rule ? this.data.rule.effectiveTo : '']
    });

    if (!isEditMode) {
      const weekDayControl = this.ruleForm.get('weekDay');
      if (weekDayControl) {
        weekDayControl.setValue(null, { emitEvent: false });
      }
    }
  }

  private resolveWeekDayOption(weekDayValue: any): any {
    if (weekDayValue && typeof weekDayValue === 'object' && weekDayValue.id !== undefined) {
      return weekDayValue;
    }

    const normalizedWeekDay = Number(weekDayValue);
    if (Number.isNaN(normalizedWeekDay)) {
      return weekDayValue;
    }

    return this.data.weekDays.find(day => Number(day.id) === normalizedWeekDay) || weekDayValue;
  }

  get weekDaysControl(): UntypedFormControl {
    return this.ruleForm.get('weekDays') as UntypedFormControl;
  }

  get isEditMode(): boolean {
    return !!this.data.rule;
  }

  onCancel(): void {
    // Cerrar sin devolver datos (el componente padre trata cualquier falsy
    // como "cancelado" y no dispara el guardado).
    this.dialogRef.close();
  }

  onSave(): void {
    if (this.ruleForm.valid) {
      const formValue = this.ruleForm.value;

      if (!this.isEditMode) {
        const selectedWeekDays = (formValue.weekDays || [])
          .map((item: any) => item && item.id !== undefined ? Number(item.id) : Number(item))
          .filter((value: number) => !Number.isNaN(value));

        const transformedBulkValue: BulkAvailabilityRuleDialogResult = {
          weekDays: Array.from(new Set(selectedWeekDays)),
          startTime: formValue.startTime,
          endTime: formValue.endTime,
          effectiveFrom: this.formatDateToDateOnly(formValue.effectiveFrom),
          effectiveTo: this.formatDateToDateOnly(formValue.effectiveTo)
        };

        this.dialogRef.close(transformedBulkValue);
        return;
      }
      
      const transformedValue = {
        ...formValue,
        weekDay: typeof formValue.weekDay === 'object' && formValue.weekDay && formValue.weekDay.id 
          ? formValue.weekDay.id 
          : formValue.weekDay,
        effectiveFrom: this.formatDateToDateOnly(formValue.effectiveFrom),
        effectiveTo: this.formatDateToDateOnly(formValue.effectiveTo)
      };
      
      this.dialogRef.close(transformedValue);
    } else {
      this.markFormGroupTouched(this.ruleForm);
    }
  }

  private formatDateToDateOnly(value: any): string | null {
    if (!value) {
      return null;
    }
    
    if (value instanceof Date) {
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, '0');
      const day = String(value.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    
    if (typeof value === 'string' && value.trim() !== '') {
      return value;
    }
    
    return null;
  }

  private getFormValidationErrors(): any {
    const errors: any = {};
    Object.keys(this.ruleForm.controls).forEach(key => {
      const controlErrors = this.ruleForm.get(key).errors;
      if (controlErrors != null) {
        errors[key] = controlErrors;
      }
    });
    return errors;
  }

  private markFormGroupTouched(formGroup: UntypedFormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control.markAsTouched();
    });
  }
}
